import {
  addDays,
  addHours,
  addMinutes,
  addSeconds,
  endOfMonth,
  format,
  isValid,
  parse,
  parseISO,
  setHours,
  setMinutes,
  setSeconds,
  startOfMonth,
  subDays,
  subMonths
} from 'date-fns';
// eslint-disable-next-line @typescript-eslint/no-require-imports
const { toZonedTime, getTimezoneOffset } = require('date-fns-tz');
import { stringify } from 'query-string';
import type { MetaWidget } from '../types/widgets';
import { chartIcons } from '../components/baseChart/utils';

const convertDateTimeToTZEpoch = ({
  dateString,
  tzOffset = '+00:00',
  timeMode = 'start',
  timeRange = [
    [0, 0],
    [23, 59]
  ]
}: {
  dateString: string;
  tzOffset?: string;
  timeMode?: 'start' | 'end';
  timeRange?: [[number, number], [number, number]];
}) => {
  // Parse the date string into year, month, and day
  const [year, month, day] = dateString.split('-').map(Number);

  // Convert the timezone offset to seconds and milliseconds
  const TZOffsetS = timezoneOffsetToSeconds(tzOffset);
  const TZOffsetMs = TZOffsetS * 1000;

  // Compute the UTC timestamp corresponding to the date at midnight in the target timezone
  let epochTimestamp = Date.UTC(year, month - 1, day) - TZOffsetMs;
  epochTimestamp /= 1000; // Convert to seconds

  // Determine the time based on timeMode and timeRange
  const timeArr = timeMode === 'start' ? timeRange[0] : timeRange[1];
  const [hr, min] = timeArr;
  let seconds = hr * 3600 + min * 60;
  const isExactEndBoundary =
    timeRange[0][0] === timeRange[1][0] && timeRange[0][1] === timeRange[1][1];
  if (timeMode !== 'start' && !isExactEndBoundary) {
    seconds += 59;
  }

  // Add the seconds to the epoch timestamp
  return epochTimestamp + seconds;
};

/**
 * Converts a date range to their corresponding Unix epoch times.
 * @param dateRange Array of two strings representing the start and end dates in ISO format.
 * @param timezoneOffset time zone offset value (+05:30)
 * @returns An array containing the start and end dates as Unix epoch times.
 */
export const convertDateRangeToEpochTimestamp = (
  dateRange: [string, string], // [2024-06-01, 2024-06-05]
  timeRange: [[number, number], [number, number]] = [
    [0, 0],
    [23, 59]
  ], // [[0, 0], [23, 59]]
  timezoneOffset: string // "+04:00"
): [number, number] => {
  const startTime = timeRange[0];
  const endTime = timeRange[1];
  // Thu Jun 06 2024 00:00:00 GMT+0530 (India Standard Time)
  // let startDateInTZUTC = parseISO(dateRange[0]).getTime() / 1000;
  let startDateInTZUTC =
    addMinutes(addHours(parseISO(dateRange[0]), startTime[0]), startTime[1]).getTime() / 1000;
  // Thu Jun 06 2024 23:59:59 GMT+0530 (India Standard Time)
  const isExactEndBoundary = startTime[0] === endTime[0] && startTime[1] === endTime[1];
  let endDate = addMinutes(addHours(parseISO(dateRange[1]), endTime[0]), endTime[1]);
  if (!isExactEndBoundary) {
    endDate = addSeconds(endDate, 59);
  }
  let endDateInTZUTC = endDate.getTime() / 1000;

  // local timezone offset in seconds
  const localTZOffset = new Date().getTimezoneOffset() * 60;
  const givenTZOffset = getTimezoneOffset(timezoneOffset) / 1000;
  const TZOffsetDelta = Math.abs(localTZOffset) - Math.abs(givenTZOffset);

  // If not native timezone, convert to that timezone's epoch
  if (timezoneOffset && TZOffsetDelta != 0) {
    startDateInTZUTC = convertDateTimeToTZEpoch({
      dateString: dateRange[0],
      timeRange: timeRange,
      timeMode: 'start',
      tzOffset: timezoneOffset
    });
    endDateInTZUTC = convertDateTimeToTZEpoch({
      dateString: dateRange[1],
      timeRange: timeRange,
      timeMode: 'end',
      tzOffset: timezoneOffset
    });
  }
  return [startDateInTZUTC, endDateInTZUTC];
};

export const configureUrlWithParams = (url: string, filters: any) => {
  let queryParams = '?';
  if (filters.locations.length > 0) {
    if (queryParams.length > 1) {
      queryParams += '&';
    }
    queryParams += stringify({ locations: filters.locations }, { arrayFormat: 'comma' });
  }
  if (filters.dates[0] !== '') {
    if (queryParams.length > 1) {
      queryParams += '&';
    }
    let timeRange = filters.dates;
    if (filters.shouldUseEpocForTimeRange) {
      timeRange = convertDateRangeToEpochTimestamp(
        filters.dates,
        filters.timeRange,
        filters.timezoneOffset
      );
    }
    queryParams += stringify(
      {
        time_range: `${timeRange[0]}:${timeRange[1]}`
      },
      { encode: false }
    );
  }
  if (filters.widgets.length > 0) {
    for (let i = 0; i < filters.widgets.length; i++) {
      if (queryParams.length > 1) {
        queryParams += '&';
      }
      const ids = filters.widgets[i].data.map((w: any) => w.id);
      queryParams += stringify(
        {
          [filters.widgets[i].key]: ids
        },
        { arrayFormat: 'comma' }
      );
    }
  }
  if (
    'additionalFilters' in filters &&
    filters.additionalFilters !== undefined &&
    filters.additionalFilters.length > 0
  ) {
    for (let i = 0; i < filters.additionalFilters.length; i++) {
      if (filters.additionalFilters[i].options) {
        if (queryParams.length > 1) {
          queryParams += '&';
        }
        const ids = filters.additionalFilters[i].options.map((d: any) => d.value);
        queryParams += stringify(
          {
            [filters.additionalFilters[i].filterKey]: ids
          },
          { encode: true, arrayFormat: 'comma' }
        );
      } else {
        // Hierarchical drill-down (area_name, etc.) and dormant-style filters:
        // encode must be true so spaces/special chars survive as one query value.
        if (queryParams.length > 1) {
          queryParams += '&';
        }
        const ids = filters.additionalFilters[i].value;
        queryParams += stringify(
          {
            [filters.additionalFilters[i].key]: ids
          },
          { encode: true }
        );
      }
    }
  }
  if ('ftsFilters' in filters) {
    const { ftsFilters } = filters;
    if (ftsFilters.length > 0) {
      for (let i = 0; i < ftsFilters.length; i++) {
        if (queryParams.length > 1) {
          queryParams += '&';
        }
        const filterQuery = ftsFilters[i].query;
        queryParams += stringify(
          {
            [`fts_${ftsFilters[i].key}`]: filterQuery
          },
          { encode: false }
        );
      }
    }
  }
  return queryParams.length > 1 ? url + queryParams : url;
};

export function removeDuplicateArrayItems(arr: any[], key = 'id') {
  const uniqueIds: Record<string, boolean> = {};
  const result: any[] = [];

  for (const item of arr) {
    const keyValue = item[key];
    if (!uniqueIds[keyValue]) {
      uniqueIds[keyValue] = true;
      result.push(item);
    }
  }
  return result;
}

export const buildFilterQueryParams = ({
  dateRange,
  timeRange
}: {
  dateRange: [string, string];
  timeRange: [[number, number], [number, number]];
}) => {
  if (!dateRange) {
    return {};
  }

  let startRange = `${dateRange[0]}`;
  let endRange = `${dateRange[1]}`;
  if (timeRange) {
    const startTimeRange = `${padNumberWithZero(
      timeRange[0][0]
    )}${padNumberWithZero(timeRange[0][1])}`;
    const endTimeRange = `${padNumberWithZero(
      timeRange[1][0]
    )}${padNumberWithZero(timeRange[1][1])}`;
    startRange = `${dateRange[0]}.${startTimeRange}`;
    endRange = `${dateRange[1]}.${endTimeRange}`;
  }

  return {
    from: startRange,
    to: endRange
  };
};

export const serializeFilterQueryParams = ({
  dateRange,
  timeRange
}: {
  dateRange: [string, string];
  timeRange: [[number, number], [number, number]];
}) => {
  const params = buildFilterQueryParams({ dateRange, timeRange });
  const queryString = stringify(params, { encode: true, sort: false });

  return queryString ? `?${queryString}` : '';
};

export const formatDateString = (dateString: string) => {
  const splitDate = dateString.split('-');
  if (splitDate.length > 1) {
    const date = new Date(
      parseInt(splitDate[0]),
      parseInt(splitDate[1]) - 1,
      parseInt(splitDate[2])
    );
    const formattedDate = format(date, 'do MMM yyyy');
    return formattedDate;
  } else {
    return dateString;
  }
};

export function numFormatter(num: number): string | number {
  const isNegative = num < 0;
  const absNum = Math.abs(num);

  // Only shorten numbers >= 10,000. Values below this show as raw numbers.
  // All shortened values use floor (not round) so displayed numbers never exceed the actual value.
  if (absNum >= 10000 && absNum < 1000000) {
    // Floor to nearest K, no decimal: 12,500 → "12K", 99,999 → "99K"
    const result = Math.floor(absNum / 1000) + 'K';
    return isNegative ? '-' + result : result;
  } else if (absNum >= 1000000 && absNum < 10000000) {
    // Floor to 1 decimal M: 1,250,000 → "1.2M", 1,000,000 → "1M"
    const floored = Math.floor(absNum / 100000) / 10;
    const result = floored % 1 === 0 ? floored + 'M' : floored.toFixed(1) + 'M';
    return isNegative ? '-' + result : result;
  } else if (absNum >= 10000000) {
    // Floor to nearest M, no decimal: 12,500,000 → "12M"
    const result = Math.floor(absNum / 1000000) + 'M';
    return isNegative ? '-' + result : result;
  }

  // < 10,000: return raw number unchanged
  return num;
}

// Decodes the payload segment of a JWT. JWT segments are base64url (RFC 7515),
// not plain base64, and the JSON inside is UTF-8. A bare atob() throws on the
// '-' / '_' characters and returns Latin-1, which mangles multi-byte claims
// such as a company_name containing a curly apostrophe.
export const parseJwt = (token: string) => {
  const base64Url = token.split('.')[1];
  if (!base64Url) {
    throw new Error('Malformed JWT: missing payload segment');
  }
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
};

export const getEnvValueFor = (envKey: string | undefined, decodeBase64: boolean = false) => {
  // For better maintainability, we let the caller of this method to pass ENV Keys as
  // they are defined in the .env file (ALL CAPS - SNAKE CASE)
  // So envKey is in the format "SAPAAD_CORE_BASE_URL"
  let reFormattedKey = 'ENV_' + envKey;
  // so now reFormattedKey is ENV_SAPAAD_CORE_BASE_URL
  const regexPattern = /(\_\w)/g;
  reFormattedKey = reFormattedKey.toLocaleLowerCase().replace(regexPattern, function (k) {
    return k[1].toUpperCase();
  });
  // First lower case all, then camel case.
  // now reFormattedKey is envSapaadCoreBaseUrl
  const envValue = document.getElementById('root')?.dataset[reFormattedKey];
  if (decodeBase64) {
    try {
      if (envValue) {
        return atob(envValue);
      }
    } catch (e: any) {
      console.error('[ENV] Error decoding base64 for ' + envKey, e);
      return undefined;
    }
  }
  if (envValue === undefined) {
    console.error('[ENV] CANNOT FIND ENV FOR ' + envKey);
  }
  return envValue;
};

export const getAnalyticsMetaWidgets = (subWidgets: MetaWidget[]) => {
  const widgetsWithGroups = subWidgets.filter((d) => Object.keys(d.group).length > 0);
  const uniqueKeysFromWidgetGroups = [
    ...Array.from(
      new Set(
        widgetsWithGroups
          .map((d) => d.group)
          .map((d) => d.key)
          .filter(Boolean)
      )
    )
  ];
  const groupWidgets: any[] = uniqueKeysFromWidgetGroups.map((key) => {
    const groupWidget = {
      title: '',
      url: '',
      render_type: '',
      type: '',
      sub_widgets: []
    };
    const groupWithSameKey = widgetsWithGroups.filter((w) => w.group.key === key);
    if (groupWithSameKey.length > 0) {
      groupWidget.title = groupWithSameKey[0].group.title || '';
      groupWidget.url = (groupWithSameKey[0].group.key || '').toLowerCase().replace(' ', '_');
      groupWidget.render_type = 'group';
      groupWidget.type = groupWithSameKey[0].group.type || '';
      groupWidget.sub_widgets = (groupWithSameKey?.map((w) => ({
        type: w.type,
        url: w.url
      })) || []) as any;
    }
    return groupWidget;
  });
  const widgets = subWidgets
    .filter((d) => !(Object.keys(d.group).length > 0))
    .map((d) => ({ ...d, render_type: 'single' }));
  return [...widgets, ...groupWidgets];
};

export const createWidgetFilterConfig = (widgetData: any) => {
  const config = {
    enableFilters: false,
    enableInteractions: false,
    widgetFilterKey: '',
    filterTitle: ''
  };
  if (widgetData.widget_filter_config) {
    config.enableFilters = Boolean(widgetData.widget_filter_config.key);
    config.enableInteractions = Boolean(widgetData.widget_filter_config.key);
    config.filterTitle = widgetData.widget_filter_config.heading;
    config.widgetFilterKey = widgetData.widget_filter_config.key;
  }
  return config;
};

export const swrDefaultConfig = {
  refreshWhenOffline: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false
};

export const createDecimalNumberFormatter = (decimalPlaces: number) => {
  return (value: any) => {
    if (typeof value === 'string') {
      return parseFloat(value).toFixed(decimalPlaces);
    }
    try {
      return value.toFixed(decimalPlaces);
    } catch {
      return parseFloat(value).toFixed(decimalPlaces);
    }
  };
};

export const filterLocationsBySelectedDateRange = (
  companyLocations: any[],
  selectedDateRange: any
) => {
  const parsedSelectedStartDate = parse(selectedDateRange[0], 'yyyy-MM-dd', new Date()).getTime();
  const validLocations = companyLocations.filter((d: any) => {
    if (!d.end_date) {
      return d;
    }
    const parsedLocationEndDate = parse(d.end_date, 'yyyy-MM-dd', new Date()).getTime();
    return parsedSelectedStartDate <= parsedLocationEndDate;
  });
  return validLocations;
};

export const dataZoomSliderConfig = (limit: any) => {
  return [
    {
      type: 'slider',
      height: 20,
      borderColor: 'none',
      startValue: 0,
      endValue: limit ?? 40,
      moveHandleSize: -20,
      backgroundColor: 'none',
      fillerColor: '#F1EDED',
      moveHandleStyle: {
        color: 'grey',
        handleSize: 5
      },
      dataBackground: {
        areaStyle: {
          color: 'none',
          opacity: 0
        },
        lineStyle: {
          color: 'none',
          opacity: 0
        }
      },
      z: 2,
      handleIcon: 'circle',
      handleSize: 25,
      moveHandleIcon: chartIcons.scrollbarMoveHandle
    },
    {
      type: 'slider',
      height: 5,
      z: 0,
      startValue: 0,
      endValue: 40,
      moveHandleSize: -20,
      backgroundColor: '#F1EDED',
      moveHandleStyle: {
        color: 'grey',
        handleSize: 5
      },
      dataBackground: {
        areaStyle: {
          color: 'none',
          opacity: 0
        },
        lineStyle: {
          color: 'none',
          opacity: 0
        }
      },
      handleIcon: '',
      handleSize: 0,
      brushSelect: false
    }
  ];
};

export const formatText = (text: string): string => {
  if (text.includes(' ')) {
    const words = text.split(' ');
    for (let i = 0; i < words.length; i++) {
      words[i] = words[i].charAt(0).toUpperCase() + words[i].slice(1);
    }
    const formattedText = words.join(' ');
    return formattedText;
  } else {
    return text;
  }
};

/**
 * Applies timezone offset to given date with the offset value
 * @param sourceDate - In epoch timestamp (seconds)
 * @param offset - Offset value ex. 'UTC+04:00'
 *
 * returns `formatted date`
 */
export const formatDateWithTimezoneOffset = (
  timestampS: any,
  timeZoneOffset: any,
  formatString = 'yyyy-MM-dd HH:mm:ss'
) => {
  const [, sign, offsetHours, offsetMinutes] = timeZoneOffset.match(/([+-])(\d{2}):(\d{2})/);
  const totalOffsetInSeconds =
    (parseInt(offsetHours, 10) * 60 * 60 + parseInt(offsetMinutes, 10) * 60) *
    (sign === '-' ? -1 : 1);
  const timestampTZ = parseInt(timestampS) + totalOffsetInSeconds;
  const absoluteDate = new Date(timestampTZ * 1000);
  const day = absoluteDate.getUTCDate();
  const month = absoluteDate.getUTCMonth();
  const year = absoluteDate.getUTCFullYear();
  const hours = absoluteDate.getUTCHours();
  const minutes = absoluteDate.getUTCMinutes();
  const seconds = absoluteDate.getUTCSeconds();
  const milliseconds = absoluteDate.getUTCMilliseconds();

  const convertedDate = new Date(year, month, day, hours, minutes, seconds, milliseconds);
  return format(convertedDate, formatString);
};

export function timezoneOffsetToSeconds(offset: string): number {
  const regex = /^([+-])(\d{2}):(\d{2})$/;
  const match = offset.match(regex);
  if (!match) {
    throw new Error('Invalid timezone format. Expected format: ±HH:MM');
  }
  const sign = offset[0] === '-' ? -1 : 1;
  const [hours, minutes] = offset.slice(1).split(':').map(Number);
  return sign * (hours * 3600 + minutes * 60);
}

export function createCompanyDateSelectionPresets({
  businessEndTimeInSecs = 0, // seconds from UTC 00:00
  timezoneOffset = '+00:00'
}: {
  businessEndTimeInSecs?: number;
  timezoneOffset?: string;
}) {
  const {
    businessEndHour,
    businessEndMinute,
    offsetDayStart,
    offsetDayEnd,
    dateTimeinTZ,
    todayBet
  } = getCompanyBusinessDayInfo({ businessEndTimeInSecs, timezoneOffset });

  const yesterdayBet = setHours(
    setMinutes(subDays(dateTimeinTZ, 1), businessEndMinute),
    businessEndHour
  );

  const prevMonthFirstDayBet = setMinutes(
    setHours(startOfMonth(subMonths(dateTimeinTZ, 1)), businessEndHour),
    businessEndMinute
  );

  const prevMonthLastDayBet = setMinutes(
    setHours(endOfMonth(subMonths(dateTimeinTZ, 1)), businessEndHour),
    businessEndMinute
  );

  const thisMonthFirstDayBet = setMinutes(
    setHours(startOfMonth(dateTimeinTZ), businessEndHour),
    businessEndMinute
  );

  const thisMonthLastDayBet = setMinutes(
    setHours(endOfMonth(dateTimeinTZ), businessEndHour),
    businessEndMinute
  );

  return {
    today: {
      title: 'Today',
      date_range: [
        format(addDays(todayBet, offsetDayStart), 'yyyy-MM-dd'),
        format(addDays(todayBet, offsetDayEnd), 'yyyy-MM-dd')
      ] as [string, string],
      time_range: [
        [businessEndHour, businessEndMinute],
        [businessEndHour, businessEndMinute]
      ] as [[number, number], [number, number]]
    },
    yesterday: {
      title: 'Yesterday',
      date_range: [
        format(addDays(yesterdayBet, offsetDayStart), 'yyyy-MM-dd'),
        format(addDays(yesterdayBet, offsetDayEnd), 'yyyy-MM-dd')
      ] as [string, string],
      time_range: [
        [businessEndHour, businessEndMinute],
        [businessEndHour, businessEndMinute]
      ] as [[number, number], [number, number]]
    },
    lastMonth: {
      title: 'Last Month',
      date_range: [
        format(addDays(prevMonthFirstDayBet, offsetDayStart), 'yyyy-MM-dd'),
        format(addDays(prevMonthLastDayBet, offsetDayEnd), 'yyyy-MM-dd')
      ] as [string, string],
      time_range: [
        [businessEndHour, businessEndMinute],
        [businessEndHour, businessEndMinute]
      ] as [[number, number], [number, number]]
    },
    thisMonth: {
      title: 'This Month',
      date_range: [
        format(addDays(thisMonthFirstDayBet, offsetDayStart), 'yyyy-MM-dd'),
        format(addDays(thisMonthLastDayBet, offsetDayEnd), 'yyyy-MM-dd')
      ] as [string, string],
      time_range: [
        [businessEndHour, businessEndMinute],
        [businessEndHour, businessEndMinute]
      ] as [[number, number], [number, number]]
    }
  };
}

export const padNumberWithZero = (value: number) => {
  return value.toString().padStart(2, '0');
};

export const generateRandomId = (length = 10) => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
};

/**
 * Validates a time range string in the format "HHMM".
 * @param timeRange - A string representing time in 24-hour format
 * (e.g., "0000" for midnight, "2359" for 11:59 PM).
 * @param defaultTimeRange - The default time range to return if the input is invalid.
 * @returns The validated time range or the default if invalid.
 */
export const validateUrlTimeValue = ({ timeRange = '0000', defaultTimeRange = '0000' }) => {
  const hours = parseInt(timeRange.slice(0, 2));
  const minutes = parseInt(timeRange.slice(2, 4));
  const isInvalidTime =
    isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59;

  if (isInvalidTime) {
    return defaultTimeRange; // Return default value for invalid input
  }
  const hourString = hours.toString().padStart(2, '0');
  const minuteString = minutes.toString().padStart(2, '0');
  return `${hourString}${minuteString}`;
};

export const convertUrlTimeValueToHrMin = (timeRange: string) => {
  const hour = parseInt(timeRange.slice(0, 2));
  const minute = parseInt(timeRange.slice(2, 4));
  return [hour, minute];
};

/**
 * Validates and processes URL date-time filters.
 *
 * @param {Object} params - The input parameters
 * @param {string[]} params.defaultDateRange - Default start and end dates
 * @param {number[][]} params.defaultTimeRange - Default start/end times as [hour, minute] arrays
 * @param {string} [params.startRange] - Start range from URL in format "YYYY-MM-DD.HHMM"
 * @param {string} [params.endRange] - End range from URL in format "YYYY-MM-DD.HHMM"
 * @param callback - Function to call with processed date and time ranges
 *
 * This function parses and validates date-time ranges from URL parameters.
 * It falls back to default values if URL parameters are missing or invalid.
 * The function handles both date and time components, ensuring correct format.
 * After processing, it calls the provided callback function with the validated ranges.
 */
export const validateUrlDateTimeFilters = (
  {
    defaultDateRange,
    defaultTimeRange,
    startRange,
    endRange
  }: {
    defaultDateRange: any;
    defaultTimeRange: any;
    startRange?: any;
    endRange?: any;
  },
  callback: (params: {
    startDateRange: any;
    endDateRange: any;
    startTimeRange: any;
    endTimeRange: any;
  }) => void = () => {}
) => {
  const [startDate, endDate] = defaultDateRange;
  const [startTime, endTime] = defaultTimeRange;
  const [startHour, startMinute] = startTime;
  const [endHour, endMinute] = endTime;
  let startDateRange = startRange?.split('.')[0] ?? startDate;
  let endDateRange = endRange?.split('.')[0] ?? endDate;
  const startTimeRangeValue = startRange?.split('.')[1];
  const endTimeRangeValue = endRange?.split('.')[1];
  const defaultStartTimeRange = `${padNumberWithZero(startHour)}${padNumberWithZero(startMinute)}`;
  const defaultEndTimeRange = `${padNumberWithZero(endHour)}${padNumberWithZero(endMinute)}`;

  let startTimeRange;
  let endTimeRange;

  if (startTimeRangeValue) {
    startTimeRange = convertUrlTimeValueToHrMin(
      validateUrlTimeValue({
        timeRange: startTimeRangeValue,
        defaultTimeRange: defaultStartTimeRange
      })
    );
  } else {
    startTimeRange = startTime;
  }
  if (endTimeRangeValue) {
    endTimeRange = convertUrlTimeValueToHrMin(
      validateUrlTimeValue({
        timeRange: endTimeRangeValue,
        defaultTimeRange: defaultEndTimeRange
      })
    );
  } else {
    endTimeRange = endTime;
  }

  const [defaultStartDate, defaultEndDate] = defaultDateRange;
  const parsedStartDate = parse(startDateRange, 'yyyy-MM-dd', new Date());
  const parsedEndDate = parse(endDateRange, 'yyyy-MM-dd', new Date());
  const isStartValid =
    isValid(parsedStartDate) && format(parsedStartDate, 'yyyy-MM-dd') === startDateRange;
  const isEndValid = isValid(parsedEndDate) && format(parsedEndDate, 'yyyy-MM-dd') === endDateRange;

  if (!isStartValid || !isEndValid) {
    startDateRange = defaultStartDate;
    endDateRange = defaultEndDate;
    startTimeRange = startTime;
    endTimeRange = endTime;
    callback({ startDateRange, endDateRange, startTimeRange, endTimeRange });
    return;
  }

  const startDateTime = setMinutes(setHours(parsedStartDate, startTimeRange[0]), startTimeRange[1]);
  const endDateTime = setMinutes(setHours(parsedEndDate, endTimeRange[0]), endTimeRange[1]);

  if (startDateTime.getTime() > endDateTime.getTime()) {
    startDateRange = defaultStartDate;
    endDateRange = defaultEndDate;
    startTimeRange = startTime;
    endTimeRange = endTime;
  }
  callback({ startDateRange, endDateRange, startTimeRange, endTimeRange });
};

(globalThis as any).formatNumber = function (num: number) {
  return typeof num === 'string'
    ? parseFloat(num).toLocaleString('en-US')
    : num.toLocaleString('en-US');
};

export const saveDecimalPrecision = (precision: number = 2) => {
  (globalThis as any).decimalPrecision = precision;
};

export const formatBytes = (bytes: number) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

/**
 * Gets today's date in the specified timezone
 * @param timezoneOffset - Timezone offset string (e.g., "+05:30", "-08:00")
 * @returns Today's date formatted as "yyyy-MM-dd" in the specified timezone
 */
export const getTodayInTimezone = (timezoneOffset?: string): string => {
  const currentDate = new Date();
  const timezoneAdjustedDate = timezoneOffset
    ? toZonedTime(currentDate, timezoneOffset)
    : currentDate;
  return format(timezoneAdjustedDate, 'yyyy-MM-dd');
};

/**
 * Calculates the company's business day date range considering business end time
 * @param businessEndTimeInSecs - Business end time in seconds from UTC 00:00
 * @param timezoneOffset - Company timezone offset (e.g., "+05:30")
 * @returns Object with today's business date range and other properties
 */
export const getCompanyBusinessDayInfo = ({
  businessEndTimeInSecs = 0,
  timezoneOffset = '+00:00'
}: {
  businessEndTimeInSecs?: number;
  timezoneOffset?: string;
}) => {
  const offsetS = timezoneOffsetToSeconds(timezoneOffset);
  const businessEndSecs = businessEndTimeInSecs + offsetS;

  let businessEndHour = Math.floor(businessEndSecs / 3600);
  let businessEndMinute = Math.floor((businessEndSecs % 3600) / 60);

  if (businessEndMinute < 0) {
    businessEndMinute = 60 - Math.abs(businessEndMinute);
  }
  if (businessEndHour >= 24) {
    businessEndHour = businessEndHour - 24;
  }
  if (businessEndHour < 0) {
    businessEndHour = 24 - Math.abs(businessEndHour);
  }

  // Current date in company timezone
  const dateTimeinTZ =
    timezoneOffset !== '+00:00' ? toZonedTime(new Date(), timezoneOffset) : new Date();

  const currentHours = dateTimeinTZ.getHours();
  const currentMinutes = dateTimeinTZ.getMinutes();

  // Determine day offset based on business end time
  let offsetDayStart = 0;
  let offsetDayEnd = 1;

  // If current time is before business end time, we're still in the previous business day
  if (
    currentHours < businessEndHour ||
    (currentHours === businessEndHour && currentMinutes < businessEndMinute)
  ) {
    offsetDayStart = -1;
    offsetDayEnd = 0;
  }

  const todayBet = setHours(
    setMinutes(setSeconds(dateTimeinTZ, 0), businessEndMinute),
    businessEndHour
  );

  return {
    businessEndHour,
    businessEndMinute,
    offsetDayStart,
    offsetDayEnd,
    dateTimeinTZ,
    todayBet,
    todayBusinessDateRange: [
      format(addDays(todayBet, offsetDayStart), 'yyyy-MM-dd'),
      format(addDays(todayBet, offsetDayEnd), 'yyyy-MM-dd')
    ] as [string, string]
  };
};

/**
 * Checks if the selected date range matches the company's "Today" business day
 * @param selectedDateRange - The selected date range [startDate, endDate]
 * @param businessEndTimeInSecs - Business end time in seconds from UTC 00:00
 * @param timezoneOffset - Company timezone offset (e.g., "+05:30")
 * @returns true if the selected date range matches today's business day
 */
export const isSelectedDateRangeToday = (
  selectedDateRange: [string, string],
  businessEndTimeInSecs = 0,
  timezoneOffset = '+00:00'
): boolean => {
  const { todayBusinessDateRange } = getCompanyBusinessDayInfo({
    businessEndTimeInSecs,
    timezoneOffset
  });

  return (
    selectedDateRange[0] === todayBusinessDateRange[0] &&
    selectedDateRange[1] === todayBusinessDateRange[1]
  );
};

/**
 * Generates initials from a name
 * @param name - The name to generate initials from
 * @returns The initials
 */
export const getInitials = (name: string): string => {
  if (!name) return '';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};
